<script setup lang="ts">
import { computed } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { LANG_FRENCH } from '@/constants';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';
import CityType from '@/components/city/CityType.vue';

interface Props {
  cityInfo: any;
  validate: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{ 'update:cityInfo': [value: any] }>();

const currentLanguage = getCurrentLocale();
const cityInfoData = computed({
  get() {
    return props.cityInfo;
  },
  set(value) {
    emits('update:cityInfo', value);
  }
});
</script>

<template>
  <div class="city-info-option">
    <Fluid class="city-info">
      <div class="field grid">
        <div class="col-2">
          <label for="airportCode">{{ $t('management.air.airport_code') }} <span class="text-primary">*</span></label>
          <div :class="[{ 'special-field': currentLanguage === LANG_FRENCH }]">
            <InputText id="airportCode" class="w-full" v-model="cityInfoData.airportCode" />
          </div>
          <p class="text-red-500 pt-1" v-if="validate.airportCode.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-2">
          <label for="destSVId">{{ $t('management.air.sv_dest_id') }}</label>
          <InputText :maxLength="12" id="destSVId" :class="['w-full', { 'special-field': currentLanguage === LANG_FRENCH }]" v-model="cityInfoData.destSvId" />
        </div>
        <div class="col-2">
          <label for="parentDestId">{{ $t('management.air.parent_dest_id') }}</label>
          <InputText :maxLength="12" id="parentDestId" class="w-full" v-model="cityInfoData.parentDestId" />
        </div>
        <div class="col-6">
          <label for="referenceType">{{ $t('common.labels.record_type') }} <span class="text-primary">*</span> </label>
          <div class="city-type" :class="['city-type', { 'special-field-margin': currentLanguage === LANG_FRENCH }]">
            <CityType id="referenceType" v-model:referenceType="cityInfoData.referenceType" />
          </div>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col">
          <label for="airportNameEn">{{ $t('management.air.airport_name') }} <span class="text-primary">*</span></label>
          <InputText id="airportNameEn" class="w-full" v-model="cityInfoData.airportNameEn" />
          <p class="text-red-500 pt-1" v-if="validate.airportNameEn.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col">
          <label for="airportNameFr">{{ $t('management.air.airport_name_french') }} <span class="text-primary">*</span></label>
          <InputText id="airportNameFr" class="w-full" v-model="cityInfoData.airportNameFr" />
          <p class="text-red-500 pt-1" v-if="validate.airportNameFr.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col-6">
          <label for="cityNameEn">{{ $t('management.air.city_name') }} <span class="text-primary">*</span></label>
          <InputText id="cityNameEn" class="w-full" v-model="cityInfoData.cityNameEn" />
          <p class="text-red-500 pt-1" v-if="validate.cityNameEn.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-6">
          <label for="cityNameFr">{{ $t('management.air.city_name_french') }} <span class="text-primary">*</span></label>
          <InputText id="cityNameFr" class="w-full" v-model="cityInfoData.cityNameFr" />
          <p class="text-red-500 pt-1" v-if="validate.cityNameFr.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col-6">
          <label for="regionEn">{{ $t('management.air.region') }} <span class="text-primary">*</span></label>
          <InputText id="regionEn" class="w-full" v-model="cityInfoData.regionEn" />
          <p class="text-red-500 pt-1" v-if="validate.regionEn.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-6">
          <label for="regionFr">{{ $t('management.air.region_french') }} <span class="text-primary">*</span></label>
          <InputText id="regionFr" class="w-full" v-model="cityInfoData.regionFr" />
          <p class="text-red-500 pt-1" v-if="validate.regionFr.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col-6">
          <label for="countryNameEn">{{ $t('management.air.country_name') }} <span class="text-primary">*</span></label>
          <InputText id="countryNameEn" class="w-full" v-model="cityInfoData.countryNameEn" />
          <p class="text-red-500 pt-1" v-if="validate.countryNameEn.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-6">
          <label for="countryNameFr">{{ $t('management.air.country_name_french') }} <span class="text-primary">*</span></label>
          <InputText id="countryNameFr" class="w-full" v-model="cityInfoData.countryNameFr" />
          <p class="text-red-500 pt-1" v-if="validate.countryNameFr.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
  </div>
</template>
